import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/hooks/useAuth";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { ImpersonateUserSelector } from "@/components/admin/ImpersonateUserSelector";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { 
  LayoutDashboard, 
  Filter,
  DollarSign,
  UserCircle,
  Scale,
  MessageCircle,
  MessageSquare,
  Settings,
  Users,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Shield,
  AlertCircle,
  Phone,
  Video,
  ArrowRightLeft,
  BookOpen,
  Upload,
  FileBarChart,
  Wallet,
  Sparkles,
  ClipboardCheck,
  ClipboardList,
  Home,
  PhoneOutgoing,
  CalendarDays,
  ListChecks,
} from "lucide-react";
import divusLogo from "@/assets/divus-immigration-logo.png";
import { DivusAISheet } from "@/components/ai/DivusAISheet";
import {
  SALES_RESTRICTED_ROLES,
  USER_EMAILS,
  ALAN_VITAL_APP_USER_ID,
  SALES_REPORT_ONLY_EMAILS,
  CUSTOMER_SERVICE_BLOCKED_EMAILS,
  FINANCE_ADMIN_ALLOWED_EMAILS,
} from "@/constants/sidebarAccess";

const getNavigationItems = (t) => [
  {
    title: t('navigation.home', 'Início'),
    key: "Home",
    url: "/",
    icon: Home,
  },
  {
    title: 'Painel',
    key: "MyDashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
  },
  // TEMPORARIAMENTE OCULTO - Avaliar remoção completa após período de teste
  // {
  //   title: t('navigation.leads'),
  //   key: "Leads",
  //   url: createPageUrl("Leads"),
  //   icon: Filter,
  // },
  {
    title: t('navigation.deals'),
    key: "Deals",
    url: createPageUrl("Deals"),
    icon: DollarSign,
  },
  {
    title: t('navigation.contacts'),
    key: "Contacts",
    url: createPageUrl("Contacts"),
    icon: UserCircle,
  },
  {
    title: t('navigation.cases'),
    key: "Cases",
    url: createPageUrl("Cases"),
    icon: Scale,
  },
  {
    title: t('navigation.onboarding', 'Onboarding'),
    key: "Onboarding",
    url: "/onboarding",
    icon: ClipboardCheck,
  },
  {
    title: t('navigation.messages'),
    key: "Messages",
    url: createPageUrl("WhatsApp"),
    icon: MessageCircle,
  },
  {
    title: t('navigation.customerService', 'Atendimento'),
    key: "CustomerService",
    url: "/customer-service",
    icon: MessageSquare,
  },
  {
    title: t('navigation.calls'),
    key: "Calls",
    url: "/calls",
    icon: Phone,
  },
  {
    title: 'Prospecção',
    key: "Outbound",
    url: "/outbound",
    icon: PhoneOutgoing,
  },
  {
    title: 'Tarefas',
    key: "Agenda",
    url: "/agenda",
    icon: CalendarDays,
  },
  {
    title: 'Listas',
    key: "Lists",
    url: "/listas",
    icon: ListChecks,
  },
  {
    title: t('navigation.productFeedback'),
    key: "ProductFeedback",
    url: "/product-feedback",
    icon: MessageSquare,
  },
  {
    title: t('navigation.knowledgeBase'),
    key: "KnowledgeBase",
    url: "/knowledge-base",
    icon: BookOpen,
  },
  {
    title: t('navigation.reports'),
    key: "Reports",
    url: "/reports",
    icon: FileBarChart,
  },
];

// Finance menu items - role-based visibility handled in component
const getFinanceItems = (t) => [
  {
    title: t('navigation.finance', 'Financeiro'),
    key: "Finance",
    url: "/finance",
    icon: Wallet,
    children: [
      { title: t('navigation.financeDashboard', 'Dashboard'), url: '/finance' },
      { title: t('navigation.collection', 'Cobrança'), url: '/finance/collection' },
      { title: t('navigation.receivables', 'Contas a Receber'), url: '/finance/receivables' },
      { title: t('navigation.payables', 'Contas a Pagar'), url: '/finance/expenses' },
      { title: t('navigation.bankAccounts', 'Contas Bancárias'), url: '/finance/bank-accounts' },
      { title: t('navigation.financeReports', 'Relatórios'), url: '/finance/reports' },
    ],
  },
];

const getSettingsItems = (t) => [
  {
    title: t('navigation.settings'),
    key: "Settings",
    url: createPageUrl("Settings"),
    icon: Settings,
  },
];

export function AppSidebar() {
  const { t } = useTranslation();
  const { theme, resolvedTheme } = useTheme();
  const { open, setOpenMobile, isMobile } = useSidebar();
  
  // Fecha a sidebar no mobile após navegação
  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };
  const location = useLocation();
  const { user: authUser } = useAuth();
  const { permissions } = useUserPermissions();
  const [aiSheetOpen, setAiSheetOpen] = useState(false);


  const navigationItems = getNavigationItems(t);
  const financeItems = getFinanceItems(t);
  const settingsItems = getSettingsItems(t);

   const userRoleCode = permissions?.system_role?.code;
   const currentUserEmail = (permissions?.email || '').toLowerCase();
   const isSalesDepartment = SALES_RESTRICTED_ROLES.includes(userRoleCode);
   const isSalesDirector = userRoleCode === 'sales_director';
   const isAlanVital = permissions?.id === ALAN_VITAL_APP_USER_ID || currentUserEmail === USER_EMAILS.ALAN_VITAL;
   const isMariane = currentUserEmail === USER_EMAILS.MARIANE;
   const isJosie = currentUserEmail === USER_EMAILS.JOSIE;
   const isCarine = currentUserEmail === USER_EMAILS.CARINE;
   const hasSalesReportAccess = SALES_REPORT_ONLY_EMAILS.includes(currentUserEmail);
   const canAccessCustomerService = !CUSTOMER_SERVICE_BLOCKED_EMAILS.includes(currentUserEmail);
   const hasSalesLikeRestriction = (isSalesDepartment || isAlanVital || isMariane) && !isCarine;

   const isAdminOrIT = userRoleCode === 'admin' || userRoleCode === 'it';

   const isFinanceAdminAllowed = FINANCE_ADMIN_ALLOWED_EMAILS.includes(currentUserEmail);
   const canSeeFinance = isFinanceAdminAllowed;

   const SALES_ALLOWED_KEYS = ['Home', 'MyDashboard', 'Deals', 'Contacts', 'Messages', 'Calls', 'Agenda', 'ProductFeedback', 'KnowledgeBase', 'Outbound', 'Lists'];
   const MARIANE_ALLOWED_KEYS = ['Home', 'MyDashboard', 'Cases', 'Contacts', 'CustomerService', 'Agenda', 'ProductFeedback', 'KnowledgeBase'];
   const baseVisibleItems = isMariane
      ? navigationItems.filter(item => [...MARIANE_ALLOWED_KEYS, 'Reports'].includes(item.key))
      : hasSalesLikeRestriction
        ? navigationItems.filter(item => SALES_ALLOWED_KEYS.includes(item.key) || (item.key === 'Reports' && hasSalesReportAccess))
        : navigationItems.filter(item => item.key !== 'Reports' || isFinanceAdminAllowed || hasSalesReportAccess);

   const visibleItems = baseVisibleItems.filter(
      item => canAccessCustomerService || item.key !== 'CustomerService'
   );



  const user = authUser ? {
    company_name: 'Divus Legal Group',
    company_logo: null
  } : null;

  const isActive = (path) => {
    return location.pathname === path || location.pathname === path + '/';
  };

  const getNavClass = ({ isActive }) =>
    isActive 
      ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold" 
      : "hover:bg-sidebar-accent/50 font-medium";

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-4 py-3 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2">
        <div className="flex items-center justify-center">
          <img 
            src={divusLogo} 
            alt="Divus Immigration" 
            className="max-w-[140px] w-full h-auto object-contain group-data-[collapsible=icon]:max-w-[40px] transition-all dark:brightness-0 dark:invert"
          />
        </div>
      </SidebarHeader>

      <SidebarContent className="px-3 py-4">
        {/* Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1 group-data-[collapsible=icon]:gap-4">
              {visibleItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.url);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={active} size="default" tooltip={item.title}>
                      <NavLink to={item.url} end className={getNavClass} onClick={handleNavClick}>
                        <Icon className="!h-5 !w-5" />
                        <span className="text-sm font-inter">
                          {item.title}
                        </span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              
              {/* Finance menu - visible based on role */}
              {canSeeFinance && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname.startsWith('/finance')} size="default" tooltip={t('navigation.finance', 'Financeiro')}>
                    <NavLink to="/finance" className={getNavClass} onClick={handleNavClick}>
                      <Wallet className="!h-5 !w-5" />
                      <span className="text-sm font-inter">{t('navigation.finance', 'Financeiro')}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              
              {/* People/HR menu - visible based on role */}
              {canSeeFinance && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname.startsWith('/people')} size="default" tooltip="Pessoas">
                    <NavLink to="/people" className={getNavClass} onClick={handleNavClick}>
                      <Users className="!h-5 !w-5" />
                      <span className="text-sm font-inter">Pessoas</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              
              {/* Admin link - visible to admins, IT, sales_director and Alan Vital */}
              {(isFinanceAdminAllowed || isSalesDirector || isAlanVital || isCarine) && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive('/admin')} size="default" tooltip={t('navigation.admin')}>
                    <NavLink to="/admin" end className={getNavClass} onClick={handleNavClick}>
                      <Shield className="!h-5 !w-5" />
                      <span className="text-sm font-inter">{t('navigation.admin')}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Kommo Import & IT Backlog - only for admin/IT (except Alan Vital) */}
              {isAdminOrIT && !isAlanVital && (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/kommo-import')} size="default" tooltip="Kommo Import">
                      <NavLink to="/kommo-import" end className={getNavClass} onClick={handleNavClick}>
                        <Upload className="!h-5 !w-5" />
                        <span className="text-sm font-inter">Kommo Import</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/it-backlog')} size="default" tooltip="IT Backlog">
                      <NavLink to="/it-backlog" end className={getNavClass} onClick={handleNavClick}>
                        <ClipboardList className="!h-5 !w-5" />
                        <span className="text-sm font-inter">IT Backlog</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Divus AI */}
        <SidebarGroup className="mt-4">
          <SidebarGroupContent>
            <SidebarMenu className="gap-1 group-data-[collapsible=icon]:gap-4">
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Divus AI"
                  size="default"
                  onClick={() => setAiSheetOpen(true)}
                  className="cursor-pointer bg-gradient-to-r from-violet-500/10 to-indigo-500/10 hover:from-violet-500/20 hover:to-indigo-500/20 border border-violet-500/20"
                >
                  <Sparkles className="!h-5 !w-5 text-violet-500" />
                  <span className="text-sm font-inter font-medium bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">
                    Divus AI
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Settings - hidden for sales-like restricted users */}
        {!hasSalesLikeRestriction && (
          <SidebarGroup className="mt-4">
            <SidebarGroupLabel className="text-xs uppercase text-muted-foreground px-2 mb-2">
              {t('navigation.settings')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1 group-data-[collapsible=icon]:gap-4">
                {settingsItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.url);
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild isActive={active} size="default" tooltip={item.title}>
                        <NavLink to={item.url} end className={getNavClass} onClick={handleNavClick}>
                          <Icon className="!h-5 !w-5" />
                          <span className="text-sm font-inter">{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      
      {/* Divus AI Sheet */}
      <DivusAISheet open={aiSheetOpen} onOpenChange={setAiSheetOpen} />

      <SidebarFooter className="p-4 space-y-2">
        {(isAdminOrIT) && (
          <div className="flex justify-center group-data-[collapsible=icon]:hidden">
            <ImpersonateUserSelector />
          </div>
        )}
        <SidebarTrigger className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto transition-transform hover:scale-110 shadow-md">
          {open ? (
            <ChevronLeft className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </SidebarTrigger>
      </SidebarFooter>
    </Sidebar>
  );
}
